// =============================================================
//  RegisterScreen — Inscription (refonte split-screen dashboard)
//  Choix user / centre + champs + validation + création Firebase.
//  Logique métier et structure Firestore préservées à 100%.
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { auth, db } from '../private/firebaseConfig';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

import AuthLayout from './auth-ui/AuthLayout';
import { Input, Button, SegmentedSelect } from '../admin/web-ui/kit';
import { colors, spacing } from '../admin/web-ui/theme';

const USER_TYPES = [
  { value: 'user', label: 'Utilisateur' },
  { value: 'center', label: 'Centre / École' },
];

export default function RegisterScreen({ navigation }) {
  const [userType, setUserType] = useState('user');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [centerData, setCenterData] = useState({
    centerName: '',
    address: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);

  const showAlert = (title, message, buttons = null) => {
    if (Platform.OS === 'web') {
      if (buttons && buttons.length > 0) {
        const confirmed = window.confirm(`${title}\n\n${message}`);
        if (confirmed && buttons[0].onPress) buttons[0].onPress();
      } else {
        window.alert(`${title}\n\n${message}`);
      }
    } else {
      if (buttons) Alert.alert(title, message, buttons);
      else Alert.alert(title, message);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use': return 'Cette adresse email est déjà utilisée.';
      case 'auth/invalid-email': return "L'adresse email est invalide.";
      case 'auth/operation-not-allowed': return "L'inscription est temporairement désactivée.";
      case 'auth/weak-password': return 'Mot de passe trop faible (6 caractères min.)';
      case 'firestore/permission-denied': return 'Erreur de permission lors de la création du profil.';
      case 'network-error': return "Problème de connexion. Réessaie plus tard.";
      default: return 'Une erreur est survenue. Réessaie plus tard.';
    }
  };

  const validate = () => {
    const e = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{9}$/;

    if (!formData.email) e.email = "L'email est requis";
    else if (!emailRegex.test(formData.email)) e.email = "Format d'email invalide";

    if (!formData.phone) e.phone = 'Le téléphone est requis';
    else if (!phoneRegex.test(formData.phone)) e.phone = '9 chiffres requis';

    if (!formData.password) e.password = 'Le mot de passe est requis';
    else if (formData.password.length < 6) e.password = 'Au moins 6 caractères';

    if (formData.password !== formData.confirmPassword) {
      e.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (userType === 'user') {
      if (!formData.fullName) e.fullName = 'Le nom complet est requis';
    } else {
      if (!centerData.centerName) e.centerName = 'Le nom du centre est requis';
      if (!centerData.address) e.address = "L'adresse est requise";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const createUserProfile = async (userId, userData) => {
    try {
      const userRef = doc(db, userType === 'user' ? 'users' : 'centers', userId);
      const baseData = {
        email: userData.email,
        phone: userData.phone,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isVerified: false,
        status: userType === 'user' ? 'active' : 'pending',
        uid: userId,
      };
      const profileData =
        userType === 'user'
          ? { ...baseData, fullName: userData.fullName, userType: 'user' }
          : {
              ...baseData,
              centerName: userData.centerName,
              address: userData.address,
              userType: 'center',
              approvalStatus: 'pending',
            };
      await setDoc(userRef, profileData);
    } catch (err) {
      console.error('Error creating profile:', err);
      throw new Error('firestore/permission-denied');
    }
  };

  const handleRegister = async () => {
    setGlobalError(null);
    if (!validate()) return;
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      try {
        await sendEmailVerification(cred.user);
      } catch {}
      await createUserProfile(
        cred.user.uid,
        userType === 'user' ? formData : { ...formData, ...centerData }
      );
      showAlert(
        'Inscription réussie',
        userType === 'user'
          ? 'Compte créé ! Vérifie ton email pour activer ton compte.'
          : "Centre enregistré ! Tu pourras compléter ton profil après validation.",
        [{ text: 'OK', onPress: () => navigation.navigate('LoginScreen') }]
      );
    } catch (err) {
      console.error('Register error:', err);
      setGlobalError(getErrorMessage(err.code) || getErrorMessage(err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Créer un compte ✨"
      subtitle="Rejoins la communauté MyConcours et accède à tous les concours et fiches."
      tagline={'Lance ton compte\nen quelques secondes.'}
      description="Crée ton compte personnel pour acheter des concours, ou enregistre ton centre pour publier des annonces."
      footer={
        <View style={styles.footerRow}>
          <Text style={styles.footerText}>Tu as déjà un compte ?</Text>
          <TouchableOpacity onPress={() => navigation.navigate('LoginScreen')}>
            <Text style={styles.footerLink}>Se connecter</Text>
          </TouchableOpacity>
        </View>
      }
    >
      {!!globalError && (
        <View style={styles.errorBox}>
          <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.danger} />
          <Text style={styles.errorBoxText}>{globalError}</Text>
        </View>
      )}

      <View style={{ marginBottom: spacing.lg }}>
        <Text style={styles.label}>Type de compte</Text>
        <SegmentedSelect options={USER_TYPES} value={userType} onChange={setUserType} />
      </View>

      {userType === 'user' ? (
        <Field label="Nom complet" error={errors.fullName}>
          <Input
            icon="account-outline"
            value={formData.fullName}
            onChangeText={(t) => setFormData((s) => ({ ...s, fullName: t }))}
            placeholder="Nom et prénom"
          />
        </Field>
      ) : (
        <>
          <Field label="Nom du centre" error={errors.centerName}>
            <Input
              icon="school-outline"
              value={centerData.centerName}
              onChangeText={(t) => setCenterData((s) => ({ ...s, centerName: t }))}
              placeholder="ENS Yaoundé, IUT Douala…"
            />
          </Field>
          <Field label="Adresse" error={errors.address}>
            <Input
              icon="map-marker-outline"
              value={centerData.address}
              onChangeText={(t) => setCenterData((s) => ({ ...s, address: t }))}
              placeholder="Ville, quartier…"
            />
          </Field>
        </>
      )}

      <Field label="Email" error={errors.email}>
        <Input
          icon="email-outline"
          value={formData.email}
          onChangeText={(t) => setFormData((s) => ({ ...s, email: t }))}
          placeholder="ton.email@exemple.com"
          type="email"
          autoCapitalize="none"
        />
      </Field>

      <Field label="Téléphone" error={errors.phone}>
        <Input
          icon="phone-outline"
          value={formData.phone}
          onChangeText={(t) => setFormData((s) => ({ ...s, phone: t }))}
          placeholder="6XXXXXXXX"
          type="number"
        />
      </Field>

      <Field label="Mot de passe" error={errors.password}>
        <Input
          icon="lock-outline"
          value={formData.password}
          onChangeText={(t) => setFormData((s) => ({ ...s, password: t }))}
          placeholder="6 caractères minimum"
          type={showPassword ? 'text' : 'password'}
          rightIcon={showPassword ? 'eye-off-outline' : 'eye-outline'}
          onRightIconPress={() => setShowPassword((v) => !v)}
        />
      </Field>

      <Field label="Confirmer le mot de passe" error={errors.confirmPassword}>
        <Input
          icon="lock-check-outline"
          value={formData.confirmPassword}
          onChangeText={(t) => setFormData((s) => ({ ...s, confirmPassword: t }))}
          placeholder="Retaper le mot de passe"
          type={showConfirm ? 'text' : 'password'}
          rightIcon={showConfirm ? 'eye-off-outline' : 'eye-outline'}
          onRightIconPress={() => setShowConfirm((v) => !v)}
        />
      </Field>

      <Button
        icon="account-plus-outline"
        onPress={handleRegister}
        loading={loading}
        fullWidth
        style={{ marginTop: 8 }}
      >
        Créer mon compte
      </Button>
    </AuthLayout>
  );
}

function Field({ label, error, children }) {
  return (
    <View style={{ marginBottom: spacing.md }}>
      <Text style={styles.label}>{label}</Text>
      {children}
      {!!error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  label: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 6,
  },
  errorText: { fontSize: 12, color: colors.danger, marginTop: 4 },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.dangerSoft,
    borderWidth: 1,
    borderColor: colors.danger + '40',
    borderRadius: 10,
    padding: 12,
    marginBottom: spacing.md,
  },
  errorBoxText: { flex: 1, fontSize: 13, color: colors.danger, fontWeight: '600' },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  footerText: { color: colors.textSecondary, fontSize: 13 },
  footerLink: { color: colors.primary, fontSize: 13, fontWeight: '700' },
});
